/**
 * @file Timed stat modifiers and shields (GDD §Fase 3 — powerups).
 *
 * Buffs never overwrite base stats. Systems ask for a multiplier at the point
 * of use, so a buff expiring restores the original behaviour with no bookkeeping
 * and no chance of a stat drifting after repeated pickups.
 */

import { Component } from '../ecs/Component.js';

export const TimedStatus = Object.freeze({
  IMMORTAL: 'immortal',
  UNLIMITED_AMMO: 'unlimitedAmmo'
});

export class StatusEffects extends Component {
  constructor() {
    super();
    /**
     * Active buffs keyed by stat name.
     * @type {Map<string, { multiplier: number, remaining: number, duration: number }>}
     */
    this.active = new Map();
    /** @type {number} Damage the shield will absorb before health is touched. */
    this.shield = 0;
    /** @type {number} Seconds until the shield drops, regardless of charge left. */
    this.shieldRemaining = 0;
    /**
     * Active non-stat status effects keyed by id.
     * @type {Map<string, { remaining: number, duration: number }>}
     */
    this.timed = new Map();
  }

  /**
   * Applies or refreshes a buff. Re-picking the same buff refreshes the timer
   * and keeps the stronger multiplier rather than stacking into absurdity.
   *
   * @param {string} stat Stat name from {@link ../config/Powerups.js Stat}.
   * @param {number} multiplier Multiplier to apply.
   * @param {number} duration Seconds it lasts.
   * @returns {void}
   */
  apply(stat, multiplier, duration) {
    const existing = this.active.get(stat);
    if (existing) {
      existing.multiplier = Math.max(existing.multiplier, multiplier);
      existing.remaining = Math.max(existing.remaining, duration);
      existing.duration = Math.max(existing.duration, duration);
      return;
    }
    this.active.set(stat, { multiplier, remaining: duration, duration });
  }

  /**
   * @param {string} stat Stat name.
   * @returns {number} Current multiplier, or 1 when no buff is active.
   */
  multiplierOf(stat) {
    return this.active.get(stat)?.multiplier ?? 1;
  }

  /**
   * Applies or refreshes a non-stat status effect.
   *
   * @param {string} status Status id from {@link TimedStatus}.
   * @param {number} duration Seconds it lasts.
   * @returns {void}
   */
  applyTimed(status, duration) {
    const existing = this.timed.get(status);
    if (existing) {
      existing.remaining = Math.max(existing.remaining, duration);
      existing.duration = Math.max(existing.duration, duration);
      return;
    }
    this.timed.set(status, { remaining: duration, duration });
  }

  /**
   * @param {string} status Status id.
   * @returns {number} Seconds left, or 0 when inactive.
   */
  remainingOf(status) {
    return this.timed.get(status)?.remaining ?? 0;
  }

  /** @returns {void} */
  reset() {
    this.active.clear();
    this.shield = 0;
    this.shieldRemaining = 0;
    this.timed.clear();
  }
}

/**
 * Reads a stat multiplier off an entity that may not carry the component.
 * Keeps every caller free of null checks.
 *
 * @param {import('../ecs/Entity.js').Entity} entity Entity to inspect.
 * @param {string} stat Stat name.
 * @returns {number} Multiplier, defaulting to 1.
 */
export function statMultiplier(entity, stat) {
  return entity.get(StatusEffects)?.multiplierOf(stat) ?? 1;
}

/**
 * @param {import('../ecs/Entity.js').Entity} entity Entity to inspect.
 * @param {string} status Status id.
 * @returns {number} Seconds left, or 0 when inactive.
 */
export function timedStatusRemaining(entity, status) {
  return entity.get(StatusEffects)?.remainingOf(status) ?? 0;
}
